import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  SavedListCompanyItem,
  WorkforceActiveCompanyListToSavedList,
} from '../saved-lists/saved-lists-models';
import { NotificationService } from '../notification-service/notification.service';
import { SavedList, SavedListsService } from '../saved-lists/saved-lists.service';
import { Log } from 'src/app/logger.service';
import { SaveListModalComponent } from 'src/app/workforce/save-list-modal/save-list-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { SelectedCompany } from '../company-search/company-search.component';
import { ChatService as ChatProtoService, ChatRequest } from 'ldt-chat-proto-api';
import { Observable, switchMap, forkJoin, map, of, catchError, first } from 'rxjs';
import { CompanyInfoService } from '../company-search/company-info-service.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StoredItemsBrowserComponent } from '../components/stored-items-browser/stored-items-browser.component';
import { FindStreamByEmailRequest, StreamsOnboardingService } from 'ldt-onboarding-service-api';

export interface WorkforceActiveCompanyList {
  id: string | null; // null for draft lists, if id is set it's a saved list
  name: string;
  companies: SavedListCompanyItem[];
  scope?: string;
}

const DEFAULT_COMPANY_LIST_ID = 'c4eae0d3-74f2-47f5-8e02-0c7e64b00355'; // Big tech
const MAX_COMPANIES = 10;

@Component({
  selector: 'app-company-list-editor',
  templateUrl: './company-list-editor.component.html',
  styleUrls: ['./company-list-editor.component.scss'],
  standalone: false,
})
export class CompanyListEditorComponent {
  @ViewChild('nameInput') nameInput!: ElementRef;
  @Input() multiSelect: 'on' | 'off' | 'toggle' = 'off';
  @Input() initialActiveList: WorkforceActiveCompanyList = {
    id: null,
    name: '',
    companies: [],
  };
  @Input() initialSelectedCompanyIds: Set<string> = new Set();
  @Input() compactMode: boolean = false; // Hides action buttons (save, load, clear)
  @Input() readOnly: boolean = false; // Disables adding/removing companies
  @Input() allowSelection: boolean = true; // Allows toggling company selection
  @Output() selectedCompaniesChange = new EventEmitter<Set<SavedListCompanyItem>>();
  @Output() activeListChange = new EventEmitter<WorkforceActiveCompanyList>();

  maxCompanies = MAX_COMPANIES;

  activeList: WorkforceActiveCompanyList = {
    id: null,
    name: '',
    companies: [],
  };
  isListModified: boolean = false;
  isListNameModified: boolean = false;
  editMode: boolean = false;

  selectMultipleCompanies: boolean = false;

  isEditingName = false;
  listNameEdit = '';
  isLoadingList = false;

  selectedCompanyIds: Set<string> = new Set();
  lastSelectedCompanyId: string | undefined;

  // null indicates we have no list and are loading suggested companies, [] indicates we have no suggestions to offer (likely loading error)
  suggestedCompanies: SelectedCompany[] | null = [];

  constructor(
    private notify: NotificationService,
    private savedListsService: SavedListsService,
    private dialog: MatDialog,
    private chatProtoService: ChatProtoService,
    private companyInfoService: CompanyInfoService,
    private authService: AuthService,
    private onboardingService: StreamsOnboardingService
  ) {}

  ngOnInit() {
    if (this.initialActiveList.companies.length > 0) {
      this.activeList = this.initialActiveList;
      this.selectedCompanyIds = this.initialSelectedCompanyIds;
      this.emitSelectedCompanies();
      return;
    }

    Log.d('CompanyListEditorComponent - ngOnInit - loading from streams');
    this.loadFromStreams().subscribe({
      next: (success) => {
        if (!success) {
          this.loadFromEmailDomain().subscribe({
            next: (success) => {
              if (!success) {
                this.loadFromPrebuiltLists();
              }
            },
          });
        }
      },
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialActiveList']) {
      this.activeList = changes['initialActiveList'].currentValue;
    }
    if (changes['initialSelectedCompanyIds']) {
      this.selectedCompanyIds = changes['initialSelectedCompanyIds'].currentValue;
    }
  }

  protected companyPickedInSearch(event: SelectedCompany) {
    if (!event) return;
    if (!event.company) return;

    // Generate a unique string that incorporates whether the company is a group or not
    const companyId = event.company.id + (event.type === 'group' ? '-group' : '');

    // if company ID is duplicated, bail and show error
    if (this.activeList.companies.find((c) => c.id === companyId)) {
      this.notify.error('Company already selected. Please select a different company.');
      return;
    }

    this.addCompanyToList({
      id: event.company.id,
      name: event.company.name,
      group: event.type === 'group',
    });

    this.getSuggestedCompanies();
  }

  public selectOne() {
    this.selectedCompanyIds.clear();

    if (this.activeList.companies.length === 0) {
      return;
    }

    if (
      this.lastSelectedCompanyId &&
      this.activeList.companies.find((c) => c.id === this.lastSelectedCompanyId)
    ) {
      this.selectedCompanyIds.add(this.lastSelectedCompanyId);
    } else {
      this.selectedCompanyIds.add(this.activeList.companies[0].id!);
    }
    this.emitSelectedCompanies();
  }

  public selectAll() {
    this.selectedCompanyIds.clear();
    this.activeList.companies.forEach((c) => this.selectedCompanyIds.add(c.id!));
    this.emitSelectedCompanies();
  }

  private addCompanyToList(company: SavedListCompanyItem) {
    this.cancelEditingName();
    this.activeList.companies.push(company);
    this.emitActiveList();

    if (this.activeList.id) {
      this.isListModified = true;
    }

    if (this.multiSelect === 'on') {
      this.selectedCompanyIds.add(company.id!);
    } else {
      this.selectedCompanyIds.clear();
      this.selectedCompanyIds.add(company.id!);
    }

    this.emitSelectedCompanies();

    Log.i('After adding company - activeList:', this.activeList);
  }

  protected removeCompanyFromList(company: SavedListCompanyItem) {
    this.cancelEditingName();
    this.activeList.companies = this.activeList.companies.filter((c) => c.id !== company.id);
    if (this.selectedCompanyIds.delete(company.id!)) {
      this.emitActiveList();
      this.emitSelectedCompanies();
    }
    if (this.activeList.id) {
      this.isListModified = true;
    }
    Log.i('After removing company - activeList:', this.activeList);

    this.getSuggestedCompanies();
  }

  protected isCompanySelected(companyId: string): boolean {
    return this.selectedCompanyIds.has(companyId);
  }

  protected saveList(forceNewList: boolean = false) {
    if (!this.activeList || this.activeList.companies.length === 0) {
      this.notify.info('Please add at least one company to the list before saving');
      return;
    }

    // If forceNewList is true, we're saving as a new list
    // If the list has an ID we're saving changes to a user list
    // Otherwise, we're saving as a new list
    const idToSave = forceNewList ? null : this.activeList.id ? this.activeList.id : null;

    this.savedListsService
      .saveList(idToSave, WorkforceActiveCompanyListToSavedList(this.activeList))
      .subscribe({
        next: (response) => {
          if (response) {
            this.activeList.id = response.id;
            this.isListModified = false;
            this.isListNameModified = false;
            this.notify.success('Changes saved successfully');
          }
        },
        error: (error) => {
          this.notify.error('Error saving list. Please try again later.');
          Log.e('Error saving list:', error);
        },
      });
  }

  protected saveAsNewList() {
    if (!this.activeList) return;

    // If they already changed the name, use it
    if (this.isListNameModified) {
      this.saveList(true);
      return;
    }

    const dialogRef = this.dialog.open(SaveListModalComponent, {
      width: '400px',
      data: { list: this.activeList },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.activeList.name = result.name;
        this.saveList(true);
      }
    });
  }

  protected clearList() {
    this.cancelEditingName();
    this.selectedCompanyIds.clear();
    this.isListModified = false;
    this.isListNameModified = false;
    this.activeList = {
      id: null,
      name: '',
      companies: [],
    };
    this.suggestedCompanies = [];
    this.emitSelectedCompanies();
    this.editMode = true;
  }

  protected startEditingName() {
    this.isEditingName = true;
    this.listNameEdit = this.activeList.name || 'Untitled Company List';
    setTimeout(() => {
      const input = this.nameInput?.nativeElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  protected saveListName() {
    this.isEditingName = false;

    if (!this.listNameEdit?.trim()) {
      return;
    }

    if (this.listNameEdit.trim() === this.activeList.name) {
      return;
    }

    this.activeList.name = this.listNameEdit.trim();
    this.isListModified = true;
    this.isListNameModified = true;
  }

  protected cancelEditingName() {
    this.isEditingName = false;
    this.listNameEdit = '';
  }

  protected openSavedListsDialog() {
    this.cancelEditingName();

    const dialogConfig = {
      maxWidth: '1200px',
      width: '100%',
      height: '100%',
      maxHeight: '1000px',
      panelClass: 'saved-lists-dialog',
      backdropClass: 'tw-bg-black/80',
      data: {
        items$: this.savedListsService.lists$,
        config: this.savedListsService.getBrowserConfig(),
      },
    };

    const dialogRef = this.dialog.open(StoredItemsBrowserComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((list) => {
      if (list) {
        this.onSavedCompanyListSelected(list);
      }
    });
  }

  protected onSavedCompanyListSelected(list: SavedList) {
    if (!list) return;
    this.cancelEditingName();

    Log.i('onSavedCompanyListSelected - list scope:', list.scope);

    // Validate the list has items
    if (!list.items || list.items.length === 0) {
      this.notify.error(`Selected list "${list.name}" has no companies`);
      return;
    }

    this.isLoadingList = true;
    this.editMode = false;

    this.activeList = {
      id: list.id,
      name: list.name,
      companies: list.items,
      scope: list.scope,
    };

    // Clear current company list and selection
    this.selectedCompanyIds.clear();
    this.isListModified = false;
    this.isListNameModified = false;

    // We need to get the company name for all our companies. Our lists _should_ have that saved
    // First collect those here, then continue for those that don't have a name
    const successfulCompanies: SavedListCompanyItem[] = list.items.filter((item) => item.name);

    // Create an array of observables to fetch company details
    const companyObservables = list.items
      .filter((item) => !item.name)
      .map((item) => ({
        observable: this.companyInfoService.getCompanyInfo(item.id),
        item,
      }));

    // Wait for all company details to be fetched. If we have none to fetch, use a placeholder observable
    forkJoin(
      companyObservables.length > 0 ? companyObservables.map((x) => x.observable) : [of(undefined)]
    ).subscribe({
      next: (companies) => {
        let failedCompanyCount = 0;

        // Skip processing if we had no companies to fetch
        if (companyObservables.length === 0) {
          this.activeList.companies = successfulCompanies;
          this.emitActiveList();
          this.isListModified = false;
          this.isListNameModified = false;
          this.selectDefaultCompanies();
          this.isLoadingList = false;
          return;
        }

        companies.forEach((companyDetails, index) => {
          if (companyDetails) {
            const item = companyObservables[index].item;
            // Cast the API company type to our local Company interface
            const company: SavedListCompanyItem = {
              id: companyDetails.id,
              name: companyDetails.name,
              group: item.group || false,
            };
            successfulCompanies.push(company);
          } else {
            failedCompanyCount++;
          }
        });

        // If all companies failed to load, show error and reset state
        if (successfulCompanies.length === 0) {
          this.notify.error(`Failed to load any companies from the list "${list.name}"`);
          this.activeList = {
            id: null,
            name: '',
            companies: [],
          };
          this.emitActiveList();
          this.isLoadingList = false;
          return;
        }

        // If some companies failed to load, show warning
        if (failedCompanyCount > 0) {
          this.notify.warning(`Failed to load ${failedCompanyCount} companies from "${list.name}"`);
        }

        this.activeList.companies = successfulCompanies;
        this.emitActiveList();

        // Store original list state for modification tracking
        this.isListModified = false;
        this.isListNameModified = false;
        this.selectDefaultCompanies();

        this.isLoadingList = false;
      },
      error: (error) => {
        this.notify.error(`Failed to load company details from the list "${list.name}"`);
        Log.e('Error loading company details:', error);

        // Reset state on error
        this.activeList = {
          id: null,
          name: '',
          companies: [],
        };
        this.emitActiveList();
        // this.selectedCompanyIds.clear();
        this.isLoadingList = false;
      },
    });
  }

  private selectDefaultCompanies() {
    this.selectedCompanyIds.clear();
    if (this.activeList.companies.length === 0) {
      this.lastSelectedCompanyId = undefined;
      this.emitSelectedCompanies();
      return;
    }

    if (
      this.lastSelectedCompanyId &&
      !this.activeList.companies.find((c) => c.id === this.lastSelectedCompanyId)
    ) {
      this.lastSelectedCompanyId = undefined;
    }

    if (this.multiSelect === 'on') {
      this.activeList.companies.forEach((c) => this.selectedCompanyIds.add(c.id!));
    } else {
      if (this.lastSelectedCompanyId) {
        this.selectedCompanyIds.add(this.lastSelectedCompanyId);
      } else {
        this.selectedCompanyIds.add(this.activeList.companies[0].id!);
      }
    }

    this.emitSelectedCompanies();
  }

  protected toggleEditMode() {
    this.cancelEditingName();
    this.editMode = !this.editMode;

    if (this.editMode) {
      this.getSuggestedCompanies();
    }
  }

  private multiSelectCompanies(event?: MouseEvent): boolean {
    if (this.multiSelect === 'on') {
      return true;
    }
    if (this.multiSelect === 'off') {
      return false;
    }

    if (event?.ctrlKey || event?.metaKey || this.selectMultipleCompanies) {
      this.selectMultipleCompanies = true;
      return true;
    }

    return false;
  }

  protected toggleCompanySelection(company: SavedListCompanyItem, event?: MouseEvent) {
    if (!this.multiSelectCompanies(event)) {
      this.selectedCompanyIds.clear();
      this.selectedCompanyIds.add(company.id!);
      this.lastSelectedCompanyId = company.id!;
    } else {
      if (this.selectedCompanyIds.has(company.id!)) {
        this.selectedCompanyIds.delete(company.id!);
      } else {
        this.selectedCompanyIds.add(company.id!);
        this.lastSelectedCompanyId = company.id!;

        // If user used Cmd/Ctrl + click to select multiple companies, automatically check the checkbox
        // if (
        //   this.mode === 'company' &&
        //   (event?.ctrlKey || event?.metaKey) &&
        //   !this.selectMultipleCompanies
        // ) {
        //   this.selectMultipleCompanies = true;
        // }
      }
    }

    this.emitSelectedCompanies();
  }

  protected onSelectMultipleCompaniesChange(event: any) {
    this.selectMultipleCompanies = event.checked;

    // If unchecking the checkbox, keep only the last selected company
    if (!event.checked && this.selectedCompanyIds.size > 1) {
      this.selectedCompanyIds.clear();
      if (this.lastSelectedCompanyId) {
        this.selectedCompanyIds.add(this.lastSelectedCompanyId);
      } else if (this.activeList.companies.length > 0) {
        // If no last selected company, use the first one
        this.selectedCompanyIds.add(this.activeList.companies[0].id!);
      }
    }

    this.emitSelectedCompanies();
  }

  private emitSelectedCompanies() {
    Log.i(
      'CompanyListEditorComponent - emitSelectedCompanies - selectedCompanyIds:',
      this.selectedCompanyIds
    );
    this.selectedCompaniesChange.emit(
      new Set(this.activeList.companies.filter((c) => this.selectedCompanyIds.has(c.id!)))
    );
  }

  private emitActiveList() {
    this.activeListChange.emit(this.activeList);
  }

  private getSuggestedCompanies() {
    if (this.activeList.companies.length === 0) {
      this.suggestedCompanies = [];
      return;
    }

    this.suggestedCompanies = null;
    this.getRelatedCompaniesFromChat(
      this.activeList.companies.filter((c) => c.name).map((c) => c.name!)
    ).subscribe({
      next: (companies) => {
        this.suggestedCompanies = companies;
      },
      error: (error) => {
        Log.e('WorkforceInsightsComponent getSuggestedCompanies error', error);
        this.suggestedCompanies = [];
      },
    });
  }

  // Attempts to load the user's company based on their free streams if they have one
  private loadFromStreams(): Observable<boolean> {
    const req: FindStreamByEmailRequest = {
      email: this.authService.getUserValue.email,
    };
    return this.onboardingService.findStreamByEmail(req).pipe(
      switchMap((stream) => {
        if (!stream || !stream.companies || stream.companies.length === 0) {
          return of(false);
        }

        // Create a new active list from the stream companies and save it
        const streamsActiveList: WorkforceActiveCompanyList = {
          id: null,
          name: 'Streams Free Trial Companies',
          companies: stream.companies.slice(0, MAX_COMPANIES).map((c) => ({ id: c, group: true })),
        };

        return this.savedListsService
          .saveList(null, WorkforceActiveCompanyListToSavedList(streamsActiveList))
          .pipe(
            map((response) => {
              if (response) {
                this.activeList.id = response.id;
                // this.isListModified = false;
                // this.isListNameModified = false;
                this.onSavedCompanyListSelected(response);
                return true;
              } else {
                Log.w(
                  'WorkforceInsightsComponent - saveList returned a falsy response during Streams loading:',
                  response
                );
                return false;
              }
            }),
            catchError((error) => {
              // We might not be able to save the list if an existing streams trial list is here
              // Attempt to load the "Streams Free Trial Companies" list directly
              Log.i(
                'WorkforceInsightsComponent - Failed to save Streams Free Trial Companies list, attempting to load existing one.',
                error
              );
              return this.savedListsService.companyLists$.pipe(
                first((lists) => lists !== null),
                switchMap((lists) => {
                  if (lists) {
                    const streamsList = lists.find(
                      (l) => l.name === 'Streams Free Trial Companies'
                    );
                    if (streamsList) {
                      this.onSavedCompanyListSelected(streamsList);
                      return of(true);
                    }
                  }
                  return of(false); // Streams list not found or lists array is null/empty
                }),
                catchError((err) => {
                  Log.e(
                    'WorkforceInsightsComponent - Error trying to load existing Streams Free Trial Companies list:',
                    err
                  );
                  return of(false);
                })
              );
            })
          );
      }),
      catchError((error) => {
        Log.e(
          'WorkforceInsightsComponent - Error processing Streams data for initial list setup:',
          error
        );
        return of(false);
      })
    );
  }

  // Attempts to load the user's company based on their email domain and 5 top competitors
  private loadFromEmailDomain(): Observable<boolean> {
    const email = this.authService.getUserValue?.email;
    const emailDomain = email?.split('@')[1];

    if (!emailDomain || emailDomain === 'gmail.com') {
      return of(false);
    }

    return this.companyInfoService.findMostLikelyCompanyByDomain(emailDomain).pipe(
      map((company) => {
        if (company) {
          // Don't worry about errors from this - it just means we have no companies to add right now
          this.getRelatedCompaniesFromChat([company.name!]).subscribe({
            next: (companies) => {
              companies.forEach((c) => {
                this.addCompanyToList({
                  id: c.company.id,
                  name: c.company.name,
                  group: c.type === 'group',
                });
              });
            },
          });
          const type =
            company.grouped_company_count && company.grouped_company_count > 0
              ? 'group'
              : 'company';

          this.activeList.companies.push({
            id: company.id,
            name: company.name,
            group: type === 'group',
          });
          this.emitActiveList();
          this.selectedCompanyIds.add(company.id);
          this.emitSelectedCompanies();
          return true;
        }
        return false;
      }),
      catchError((error) => {
        Log.e('CompanyListEditorComponent - loadFromEmailDomain - error:', error);
        return of(false);
      })
    );
  }

  // Loads the companies from the default list or the first one if the default list is not found
  private loadFromPrebuiltLists() {
    this.savedListsService.companyLists$
      .pipe(first((lists) => lists !== null))
      .subscribe((lists) => {
        if (lists) {
          const defaultList = lists.find((l) => l.id === DEFAULT_COMPANY_LIST_ID);
          if (defaultList) {
            this.onSavedCompanyListSelected(defaultList);
          } else {
            this.onSavedCompanyListSelected(lists[0]);
          }
        }
      });
  }

  private getRelatedCompaniesFromChat(companies: string[]): Observable<SelectedCompany[]> {
    const req: ChatRequest | any = {
      prompt: `Given the following list of companies, provide a list of 5 top companies that are related to these companies. Provide the list as a comma separated list of company names. Include only the list of company names, no other content: ${companies.join(', ')}`,
      type: 'general_chat',
      model: 'gpt-4.1-nano',
    };
    return this.chatProtoService.chat(this.authService.getSelectedOrgIdValue, req).pipe(
      switchMap((chat) => {
        Log.d('WorkforceInsightsComponent chat', chat);
        if (chat.response) {
          const companyNames = chat.response.split(',').map((c) => c.trim());
          const suggestedCompanies: SelectedCompany[] = [];
          const companyRequests = companyNames.map((c) =>
            this.companyInfoService.findMostLikelyCompanyByName(c)
          );
          return forkJoin(companyRequests).pipe(
            map((companies) => {
              companies.forEach((company) => {
                if (company) {
                  suggestedCompanies.push({
                    company: company,
                    type:
                      company.grouped_company_count && company.grouped_company_count > 0
                        ? 'group'
                        : 'company',
                  });
                }
                Log.d('WorkforceInsightsComponent suggested company', company);
              });
              return suggestedCompanies;
            })
          );
        }
        return of([]);
      })
    );
  }
}
